import { supabase } from './supabase';
import { sendServiceRequestEmail } from './email';

const TEMPLATE_KEY = 'homeowner_expense_payment_request';
const DEFAULT_APP_URL = 'https://app.honeydone.com';
const SERVICE_EMAIL = 'service@honeydone.com';

export const defaultExpensePaymentRequestSubject =
  'Payment requested for {{expenseContext}}';

export const defaultExpensePaymentRequestBody =
  'Hi {{homeownerName}},\n\n' +
  'We are requesting payment of {{amount}} for {{expenseContext}}.\n\n' +
  '{{expenseDescriptionSection}}' +
  '{{lineItemsSection}}' +
  'Please log in to {{appUrl}} to review and pay.\n\n' +
  'Thank you,\n' +
  'HoneyDone Team';

type TemplatePayload = {
  subjectTemplate: string;
  bodyTemplate: string;
};

type SendExpensePaymentRequestParams = {
  expenseId: string;
  serviceRequestId?: string;
  homeownerId: string;
  homeownerName: string | null;
  serviceRequestTitle?: string | null;
  expenseContext?: string | null;
  expenseAmount: number;
  expenseDescription: string | null;
  lineItems?: Array<{
    description: string | null;
    amount: number;
  }>;
  requestedBy: string | null;
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);

const fillTemplate = (template: string, tokens: Record<string, string>) =>
  Object.entries(tokens).reduce((result, [key, value]) => {
    const safeValue = value ?? '';
    return result.replaceAll(`{{${key}}}`, safeValue);
  }, template);

export const getExpensePaymentRequestTemplate = async (): Promise<TemplatePayload> => {
  const { data, error } = await supabase
    .from('expense_payment_email_templates')
    .select('subject_template, body_template')
    .eq('template_key', TEMPLATE_KEY)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    console.error('Error loading expense payment request template:', error);
    return {
      subjectTemplate: defaultExpensePaymentRequestSubject,
      bodyTemplate: defaultExpensePaymentRequestBody,
    };
  }

  return {
    subjectTemplate: data?.subject_template?.trim() || defaultExpensePaymentRequestSubject,
    bodyTemplate: data?.body_template?.trim() || defaultExpensePaymentRequestBody,
  };
};

export const sendExpensePaymentRequestEmail = async ({
  expenseId,
  serviceRequestId,
  homeownerId,
  homeownerName,
  serviceRequestTitle,
  expenseContext,
  expenseAmount,
  expenseDescription,
  lineItems = [],
  requestedBy,
}: SendExpensePaymentRequestParams): Promise<void> => {
  const { data: recipientEmail, error: emailError } = await supabase.rpc('get_user_email', {
    target_user_id: homeownerId,
  });

  if (emailError) {
    throw new Error(emailError.message || 'Failed to get homeowner email');
  }

  if (!recipientEmail) {
    throw new Error('No email address found for homeowner');
  }

  const { subjectTemplate, bodyTemplate } = await getExpensePaymentRequestTemplate();
  const amountLabel = formatCurrency(expenseAmount);
  const appUrl = DEFAULT_APP_URL;
  const resolvedExpenseContext = (expenseContext || serviceRequestTitle || 'your recent service').trim();
  const safeDescription = (expenseDescription || '').trim();
  const formattedLineItems = lineItems
    .map((lineItem) => {
      const label = (lineItem.description || 'Line item').trim();
      return `- ${label}: ${formatCurrency(lineItem.amount)}`;
    })
    .join('\n');
  const lineItemsSection = formattedLineItems ? `Line items:\n${formattedLineItems}\n\n` : '';

  const subject = fillTemplate(subjectTemplate, {
    homeownerName: homeownerName || 'there',
    amount: amountLabel,
    serviceRequestTitle: serviceRequestTitle || 'Service Request',
    serviceRequestId: serviceRequestId || '',
    expenseContext: resolvedExpenseContext,
    expenseDescription: safeDescription,
    expenseDescriptionSection: safeDescription ? `Expense details: ${safeDescription}\n\n` : '',
    lineItemsSection,
    appUrl,
  }).trim();

  const body = fillTemplate(bodyTemplate, {
    homeownerName: homeownerName || 'there',
    amount: amountLabel,
    serviceRequestTitle: serviceRequestTitle || 'Service Request',
    serviceRequestId: serviceRequestId || '',
    expenseContext: resolvedExpenseContext,
    expenseDescription: safeDescription,
    expenseDescriptionSection: safeDescription ? `Expense details: ${safeDescription}\n\n` : '',
    lineItemsSection,
    appUrl,
  }).trim();

  const { data: requestRow, error: requestInsertError } = await supabase
    .from('expense_payment_requests')
    .insert({
      expense_id: expenseId,
      requested_by: requestedBy,
      recipient_email: recipientEmail,
      subject,
      message_body: body,
      status: 'pending',
    })
    .select('id')
    .single();

  if (requestInsertError) {
    throw new Error(requestInsertError.message || 'Failed to record payment request');
  }

  try {
    const { data: notification, error: notificationError } = await supabase
      .from('email_notifications')
      .insert({
        service_request_id: serviceRequestId || null,
        recipient: recipientEmail,
        subject,
        body,
        status: 'pending',
      })
      .select()
      .single();

    if (notificationError) {
      throw notificationError;
    }

    await sendServiceRequestEmail(notification, {
      cc: SERVICE_EMAIL,
      replyTo: SERVICE_EMAIL,
    });

    const { error: markSentError } = await supabase
      .from('expense_payment_requests')
      .update({
        status: 'sent',
        email_notification_id: notification.id,
      })
      .eq('id', requestRow.id);

    if (markSentError) {
      console.error('Failed to mark payment request as sent:', markSentError);
    }
  } catch (error: any) {
    const { error: markFailedError } = await supabase
      .from('expense_payment_requests')
      .update({
        status: 'failed',
        error_message: error?.message || 'Failed to send email',
      })
      .eq('id', requestRow.id);

    if (markFailedError) {
      console.error('Failed to mark payment request as failed:', markFailedError);
    }
    throw error;
  }
};
